/**
 * F-88: the tab's analytics session token, issued by `POST /api/analytics/events`.
 *
 * Kept apart from the collector so the SDK resources can send it in checkout bodies
 * (`analyticsSessionId`) without importing the collector, which imports the SDK.
 * It lives in sessionStorage only: it dies with the tab and is dropped on logout.
 */

const SESSION_KEY = 'memberfy:analytics:session'
/* Server-issued tokens are 53 URL-safe characters; anything else (an old client-made id) is dropped. */
const TOKEN = /^[A-Za-z0-9_-]{40,64}$/

let memoryToken: string | null = null

export function readSessionToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored && TOKEN.test(stored)) return stored
    if (stored) sessionStorage.removeItem(SESSION_KEY)
    return null
  } catch {
    /* Storage blocked: kept for this page load only. */
    return memoryToken
  }
}

export function storeSessionToken(token: unknown) {
  if (typeof token !== 'string' || !TOKEN.test(token)) return
  memoryToken = token
  try {
    sessionStorage.setItem(SESSION_KEY, token)
  } catch {
    /* Storage blocked: the in-memory copy is enough. */
  }
}

export function clearSessionToken() {
  memoryToken = null
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* Nothing stored. */
  }
}

/** Adds `analyticsSessionId` to a checkout body when the tab has a token; the API ignores an invalid one. */
export function withAnalyticsSession<T extends object>(body: T): T & { analyticsSessionId?: string } {
  const token = readSessionToken()
  return token ? { ...body, analyticsSessionId: token } : body
}
